// Airport typeahead over the OurAirports set (round 3 §3): one component for
// signup's "home airport" and /quote's "where do you fly". Options render
// "CODE · Name · City, ST" with a closed flag (KCGX resolves and shows
// closed; that is deliberate). Free text is allowed: typing without picking
// keeps the raw code, picked options carry the airport's name too.
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api.js';

export default function AirportTypeahead({ code, onPick, placeholder = 'KPDK or DeKalb-Peachtree', inputClass = '' }) {
  const [text, setText] = useState(code || '');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => { setText(code || ''); }, [code]);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) { setOptions([]); return undefined; }
    const mySeq = ++seq.current;
    const t = setTimeout(() => {
      api.get(`/api/ctr/airports?q=${encodeURIComponent(q)}`)
        .then((r) => { if (seq.current === mySeq) { setOptions(r.airports || []); setOpen(true); } })
        .catch(() => { if (seq.current === mySeq) setOptions([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [text]);

  useEffect(() => {
    function onDoc(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function pick(a) {
    setText(a.code);
    setOpen(false);
    onPick(a);
  }

  return (
    <div ref={boxRef} className="relative min-w-0 flex-1">
      <input
        value={text}
        onChange={(e) => { setText(e.target.value.toUpperCase()); onPick({ code: e.target.value.toUpperCase().trim(), name: null, closed: false, freeText: true }); }}
        onFocus={() => { if (options.length) setOpen(true); }}
        placeholder={placeholder}
        className={inputClass || 'w-full rounded-[9px] border-[1.5px] border-line p-[12px_14px] text-sm uppercase outline-none focus:border-accent'}
      />
      {open && options.length ? (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-[260px] overflow-y-auto rounded-card-lg border border-line bg-white py-1 shadow-float">
          {options.map((a) => (
            <button key={`${a.code}-${a.name}`} type="button" onMouseDown={(e) => { e.preventDefault(); pick(a); }}
              className="flex w-full items-baseline gap-2 px-3.5 py-2 text-left hover:bg-paper">
              <span className="flex-none font-mono text-[12.5px] font-bold text-primary">{a.code}</span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                {a.name}{a.closed ? <span className="ml-1.5 rounded-pill bg-fill px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-[0.03em] text-ink-dim">closed</span> : null}
              </span>
              <span className="flex-none text-[11.5px] text-ink-faint">{[a.city, a.state].filter(Boolean).join(', ')}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
